const docs = {
  // ─── GETTING STARTED ───────────────────────────────
  "getting-started/quick-start": {
    title: "Quick Start",
    breadcrumb: ["Getting Started", "Quick Start"],
    content: `
## Get up and running in 5 minutes

Welcome to Helocore. This guide will walk you through creating your account, setting up your first project, and deploying it to the edge — all in under five minutes.

### Prerequisites

- A modern web browser (Chrome, Firefox, Safari, or Edge)
- A GitHub, GitLab, or Bitbucket account (optional, for repo sync)

### Step 1: Create your account

Visit [helocore.com](/) and click **Get started free**. You can sign up with:

- **Email and password** — Standard signup with email verification
- **GitHub OAuth** — One-click signup using your GitHub account

\`\`\`
No credit card required. The free tier includes:
- 1 project
- Shared compute (2 vCPU, 8 GB RAM)
- Community support
- Automatic SSL
\`\`\`

### Step 2: Create a project

Once logged in, click **New Project** in the dashboard. Choose from:

| Option | Description |
|--------|-------------|
| **Blank project** | Start from scratch with an empty workspace |
| **Template** | Choose from React, Next.js, Vue, Python, Go, and more |
| **Import from GitHub** | Clone an existing repository |
| **Import from GitLab** | Sync with your GitLab repos |
| **Import from Bitbucket** | Connect your Bitbucket account |

### Step 3: Write some code

The Helocore Studio editor opens automatically. You'll see:

- **File tree** on the left
- **Monaco editor** in the center
- **Live preview** on the right
- **Terminal** at the bottom

Try editing \`index.html\` or \`app.js\` — the live preview updates instantly.

### Step 4: Deploy

Click the **Deploy** button in the toolbar, or use the keyboard shortcut:

\`\`\`
Ctrl + Shift + D  (Windows/Linux)
Cmd + Shift + D   (macOS)
\`\`\`

Choose **Quick Deploy** for an instant shareable URL, or deploy to Cloudflare, Vercel, or Netlify.

### What's next?

- [Set up your account](/docs/getting-started/account-setup) — Configure 2FA, connect Git providers
- [Explore the editor](/docs/studio/editor) — Learn Monaco shortcuts and features
- [Set up AI Assistant](/docs/studio/ai-assistant) — Enable AI-powered coding
    `,
  },

  "getting-started/account-setup": {
    title: "Account Setup",
    breadcrumb: ["Getting Started", "Account Setup"],
    content: `
## Configure your Helocore account

After creating your account, we recommend setting up the following for the best experience.

### Profile settings

Navigate to **Settings → Profile** to configure:

| Field | Description |
|-------|-------------|
| Display name | Shown in collaboration sessions and commit history |
| Email | Primary contact for notifications and password reset |
| Avatar | Upload a profile picture or use your GitHub avatar |

### Connect Git providers

Helocore integrates with GitHub, GitLab, and Bitbucket. Connect them to:

- Import existing repositories
- Push and pull changes
- Create pull requests from the IDE

**To connect GitHub:**

1. Go to **Settings → Integrations**
2. Click **Connect GitHub**
3. Authorize the Helocore GitHub App
4. Select which repositories to import

**To connect GitLab or Bitbucket:**

Follow the same flow under **Settings → Integrations**. Each provider uses OAuth for secure authorization.

### Enable two-factor authentication (2FA)

We strongly recommend enabling 2FA for all accounts.

1. Go to **Settings → Security**
2. Click **Enable 2FA**
3. Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password)
4. Enter the 6-digit code to confirm
5. Save your backup codes in a secure location

\`\`\`
Supported TOTP apps:
- Google Authenticator
- Authy
- 1Password
- Microsoft Authenticator
- Any TOTP-compliant app
\`\`\`

### Notification preferences

Configure email notifications for:

- Deployment success/failure
- Collaboration invites
- Security alerts
- Billing updates

Navigate to **Settings → Notifications** to customize.
    `,
  },

  "getting-started/first-project": {
    title: "Your First Project",
    breadcrumb: ["Getting Started", "Your First Project"],
    content: `
## Create and configure your first project

### Creating a project

From the dashboard, click **New Project**. You have several options:

**Blank project:**
\`\`\`
1. Click "New Project"
2. Enter a project name
3. Select a runtime (Node.js, Python, Go, Rust, etc.)
4. Click "Create"
\`\`\`

**From a template:**
\`\`\`
Available templates:
- React (Vite)
- Next.js 14
- Vue 3
- Svelte
- Python Flask
- Python FastAPI
- Go Fiber
- Rust Actix
- Static HTML
\`\`\`

**Import from Git:**
\`\`\`
1. Click "Import Repository"
2. Select your Git provider (GitHub, GitLab, Bitbucket)
3. Choose the repository
4. Click "Import"
\`\`\`

### Project structure

Every project in Helocore gets:

| Resource | Description |
|----------|-------------|
| **Cloud compute** | Dedicated container with your chosen machine tier |
| **File system** | Persistent storage for your project files |
| **Git history** | Full version control with branch management |
| **Preview URL** | Auto-generated URL for live preview |
| **Terminal** | Full Linux terminal access |

### Project settings

Access project settings via the gear icon in the toolbar:

- **General** — Name, description, runtime
- **Environment variables** — Secrets and configuration
- **Compute** — Machine tier selection
- **Domains** — Custom domain configuration
- **Collaborators** — Invite team members
- **Danger zone** — Delete project

### Folder organization

Organize projects into folders from the dashboard:

1. Click **New Folder**
2. Name the folder
3. Drag projects into folders
4. Nest folders up to 3 levels deep
    `,
  },

  "getting-started/first-deployment": {
    title: "First Deployment",
    breadcrumb: ["Getting Started", "First Deployment"],
    content: `
## Deploy your project to the world

Helocore supports multiple deployment targets. Here's how to get your code live.

### Quick Deploy (recommended for getting started)

The fastest way to share your project:

1. Click **Deploy → Quick Deploy** in the toolbar
2. Helocore builds your project and deploys it
3. You receive a shareable public URL instantly

\`\`\`
https://your-project-abc123.helocore.app
\`\`\`

Quick Deploy features:
- Instant shareable URLs
- Versioned snapshots (v1, v2, v3...)
- One-click rollback to any version
- Automatic HTTPS

### Deploy to Cloudflare Pages

1. Go to **Deploy → Cloudflare Pages**
2. Helocore uses its platform API key (no setup needed)
3. Your project is deployed to Cloudflare's global edge network

### Deploy to Vercel

1. Go to **Deploy → Vercel**
2. Enter your Vercel API token (Settings → Deploy Tokens)
3. Click Deploy
4. Your project is live on Vercel's platform

\`\`\`
To get a Vercel token:
1. Go to vercel.com/account/tokens
2. Create a new token
3. Paste it in Helocore Settings → Deploy Tokens → Vercel
\`\`\`

### Deploy to Netlify

1. Go to **Deploy → Netlify**
2. Enter your Netlify personal access token
3. Click Deploy
4. Your project is live on Netlify

### Deploy history

Every deployment creates a versioned snapshot. Access deploy history from **Deploy → History**:

| Version | Timestamp | Status | URL |
|---------|-----------|--------|-----|
| v3 | 2 hours ago | Live | your-project.helocore.app |
| v2 | Yesterday | Available | — |
| v1 | 3 days ago | Available | — |

Click any version to rollback instantly.
    `,
  },

  // ─── EAMICO STUDIO ─────────────────────────────────
  "studio/overview": {
    title: "Helocore Studio Overview",
    breadcrumb: ["Helocore Studio", "Overview"],
    content: `
## The cloud IDE that replaces your entire local setup

Helocore Studio is a cloud-native integrated development environment that runs entirely in your browser. It combines a powerful code editor, AI assistant, terminal, live preview, Git integration, and deployment tools into a single, seamless workspace.

### Architecture

\`\`\`
┌─────────────────────────────────────────────┐
│                Helocore Studio                 │
├──────────┬──────────────┬───────────────────┤
│ File     │   Monaco     │   Live Preview    │
│ Tree     │   Editor     │   (iframe)        │
│          │              │                   │
│ Git      │   Tabs       │   Mobile /        │
│ Panel    │              │   Desktop view    │
├──────────┴──────────────┴───────────────────┤
│ Terminal │ AI Assistant │ Debugger │ Console │
└─────────────────────────────────────────────┘
\`\`\`

### Core components

| Component | Description |
|-----------|-------------|
| **Monaco Editor** | VS Code's editor engine with full syntax highlighting, IntelliSense, and multi-cursor support |
| **AI Assistant** | Multi-model AI (GPT-5.2, Claude, Gemini) with full project context |
| **AI Debugger** | Automatic error detection and fix suggestions from live preview |
| **Source Control** | Visual Git interface with branches, diffs, blame, and AI commit messages |
| **Live Preview** | Real-time preview with mobile/desktop viewports |
| **Terminal** | Full Linux terminal with split support |
| **Deployment** | One-click deploy to Cloudflare, Vercel, Netlify, or Quick Deploy |
| **Collaboration** | Real-time code editing with voice chat |

### Keyboard shortcuts

| Shortcut | Action |
|----------|--------|
| \`Ctrl+Shift+P\` | Command palette |
| \`Ctrl+Shift+D\` | Deploy |
| \`Ctrl+S\` | Save file |
| \`Ctrl+Shift+F\` | Search across files |
| \`Ctrl+\\\`\` | Toggle terminal |
| \`Ctrl+B\` | Toggle sidebar |
| \`Ctrl+Shift+G\` | Open source control |
| \`Ctrl+Shift+A\` | Open AI Assistant |

### System requirements

Helocore Studio runs in any modern browser. No downloads or local installs required.

| Browser | Minimum Version |
|---------|----------------|
| Chrome | 90+ |
| Firefox | 90+ |
| Safari | 15+ |
| Edge | 90+ |
    `,
  },

  "studio/editor": {
    title: "Editor & Monaco",
    breadcrumb: ["Helocore Studio", "Editor & Monaco"],
    content: `
## The Monaco Editor — supercharged

Helocore Studio uses the Monaco Editor, the same engine that powers Visual Studio Code. We've extended it with cloud-native features, AI integration, and real-time collaboration.

### Syntax highlighting

Monaco supports 50+ languages out of the box:

\`\`\`
JavaScript, TypeScript, Python, Go, Rust, Java, C, C++,
C#, PHP, Ruby, Swift, Kotlin, Dart, HTML, CSS, SCSS,
JSON, YAML, XML, Markdown, SQL, Shell, Docker, and more.
\`\`\`

### Multi-tab editing

- Open multiple files in tabs
- Drag tabs to reorder
- Split editor horizontally or vertically
- Pin frequently used tabs

### File tree

The left sidebar shows your complete project file tree:

| Action | How |
|--------|-----|
| Create file | Right-click → New File |
| Create folder | Right-click → New Folder |
| Rename | Right-click → Rename, or press \`F2\` |
| Delete | Right-click → Delete |
| Move | Drag and drop |

### IntelliSense & autocomplete

Monaco provides intelligent code completion for:

- **JavaScript/TypeScript** — Full type inference, import suggestions
- **Python** — Function signatures, module imports
- **CSS** — Property values, selectors
- **HTML** — Tag completion, attribute suggestions

### Git gutter decorations

The editor gutter shows real-time Git status:

| Color | Meaning |
|-------|---------|
| Green bar | New lines (added) |
| Blue bar | Modified lines |
| Red triangle | Deleted lines |

### Git blame

Enable inline blame annotations to see who wrote each line and when:

1. Right-click in the editor
2. Select **Show Git Blame**
3. Each line shows the author, date, and commit message

### Find and replace

| Shortcut | Action |
|----------|--------|
| \`Ctrl+F\` | Find in current file |
| \`Ctrl+H\` | Find and replace in current file |
| \`Ctrl+Shift+F\` | Search across all project files |
| \`Ctrl+Shift+H\` | Find and replace across all files |

Supports regular expressions, case sensitivity, and whole word matching.

### Minimap

The right-side minimap shows a bird's eye view of your file. Click anywhere on it to jump to that section.

### Keyboard shortcuts

| Shortcut | Action |
|----------|--------|
| \`Ctrl+D\` | Select next occurrence |
| \`Ctrl+Shift+L\` | Select all occurrences |
| \`Alt+Up/Down\` | Move line up/down |
| \`Ctrl+Shift+K\` | Delete line |
| \`Ctrl+/\` | Toggle comment |
| \`Ctrl+]\` | Indent line |
| \`Ctrl+[\` | Outdent line |
| \`Ctrl+G\` | Go to line |
| \`Ctrl+P\` | Quick open file |
    `,
  },

  "studio/ai-assistant": {
    title: "AI Assistant",
    breadcrumb: ["Helocore Studio", "AI Assistant"],
    content: `
## Your AI pair programmer

The Helocore AI Assistant is a context-aware coding companion that understands your entire project. It can write code, debug errors, explain logic, generate tests, and even refactor entire modules.

### Supported models

| Model | Provider | Best for |
|-------|----------|----------|
| **GPT-5.2** | OpenAI | General coding, complex reasoning |
| **Claude** | Anthropic | Long context, documentation, refactoring |
| **Gemini** | Google | Multi-modal, image analysis, fast responses |

Switch models at any time from the AI panel dropdown. All models are accessed via the Emergent LLM Key — no API keys needed.

### Context awareness

The AI Assistant automatically includes:

- **File tree** — Complete project structure
- **Open files** — All currently open editor tabs
- **Selected code** — Any highlighted text
- **Console output** — Recent terminal and preview errors
- **Git diff** — Current uncommitted changes

### Chat interface

Open the AI panel with \`Ctrl+Shift+A\` or click the AI icon in the sidebar.

**Ask questions:**
\`\`\`
"How does the authentication flow work in this project?"
"Why is this API endpoint returning a 500 error?"
"Explain this regex pattern"
\`\`\`

**Generate code:**
\`\`\`
"Create a React component for a user profile card"
"Write unit tests for the auth module"
"Add input validation to the signup form"
\`\`\`

**Refactor:**
\`\`\`
"Refactor this function to use async/await instead of callbacks"
"Split this 200-line component into smaller modules"
"Convert this JavaScript file to TypeScript"
\`\`\`

### Code editing with apply/reject

When the AI suggests code changes:

1. Changes are shown as a diff in the editor
2. Click **Apply** to accept the changes
3. Click **Reject** to discard
4. The file is locked during AI edits to prevent conflicts

### Agentic mode

For complex, multi-step tasks, use Agentic mode:

\`\`\`
"Set up a complete REST API with authentication,
user CRUD, and database integration"
\`\`\`

The AI will:
1. Plan the task
2. Create files and folders
3. Write code across multiple files
4. Run tests
5. Show you the result for approval

### Voice input

Click the microphone icon or press \`Ctrl+Shift+V\` to use voice-to-text input powered by OpenAI Whisper.

### Vision mode

Drag an image into the AI chat to analyze it:

- Screenshot of a UI → "Recreate this layout in React"
- Error screenshot → "What's causing this error?"
- Wireframe → "Implement this design"

### AI-powered generators

| Command | What it does |
|---------|-------------|
| **Generate commit message** | Analyzes your diff and writes a conventional commit message |
| **Generate README** | Creates a comprehensive README.md from your project structure |
| **Analyze project** | Full project review with suggestions for improvement |
| **Generate image** | Creates images using AI (logos, mockups, illustrations) |

### Chat history

Chat sessions are automatically saved per project. Access previous conversations from the AI panel dropdown.

- **Save** — Sessions persist across browser refreshes
- **Load** — Switch between previous sessions
- **Clear** — Start a fresh conversation
    `,
  },

  "studio/ai-debugger": {
    title: "AI Debugger",
    breadcrumb: ["Helocore Studio", "AI Debugger"],
    content: `
## Automatic error detection and resolution

The AI Debugger monitors your live preview in real time, catching errors before you even notice them.

### How it works

\`\`\`
Live Preview → Error detected → AI analyzes → Fix suggested → One-click apply
\`\`\`

1. The live preview iframe runs your application
2. Console errors are captured automatically
3. Errors are sent to the AI for analysis
4. The AI identifies the root cause
5. A fix is suggested with a one-click apply button

### Error types detected

| Type | Examples |
|------|---------|
| **Runtime errors** | TypeError, ReferenceError, null pointer |
| **Syntax errors** | Missing brackets, invalid JSX |
| **Network errors** | Failed API calls, CORS issues |
| **React errors** | Invalid hooks, missing keys, render errors |
| **CSS issues** | Layout overflow, z-index conflicts |

### Using the debugger

1. Open the **Debugger** panel at the bottom of the IDE
2. Errors appear automatically with timestamps
3. Click an error to see the AI analysis
4. Click **Apply Fix** to automatically update your code
5. The live preview reloads to verify the fix

### Error log

The error log displays:

\`\`\`
[14:32:05] TypeError: Cannot read property 'map' of undefined
           → src/components/UserList.jsx:24
           
  AI Analysis: The 'users' state is initially undefined.
  The component tries to call .map() before data loads.
  
  Suggested fix: Add a null check or initialize state as
  an empty array.
  
  [Apply Fix] [Dismiss]
\`\`\`

### Configuration

You can configure the debugger in **Settings → AI Debugger**:

- **Auto-detect** — Automatically catch errors (default: on)
- **Auto-fix** — Automatically apply fixes without confirmation (default: off)
- **Error filter** — Ignore specific error patterns
    `,
  },

  "studio/source-control": {
    title: "Source Control",
    breadcrumb: ["Helocore Studio", "Source Control"],
    content: `
## Visual Git interface

Helocore Studio provides a full-featured Git interface without ever touching the command line.

### Git panel

Open the Source Control panel with \`Ctrl+Shift+G\`. It shows:

- **Changed files** — Staged and unstaged changes
- **Commit history** — List of all commits on the current branch
- **Branches** — Create, switch, and delete branches

### Committing changes

1. Open Source Control panel
2. Review changed files (click to see diff)
3. Stage files by clicking the **+** icon
4. Enter a commit message
5. Click **Commit**

Or use **AI Commit Message** — click the sparkle icon and the AI writes a conventional commit message based on your changes.

\`\`\`
AI-generated commit messages follow Conventional Commits:

feat: add user authentication with JWT
fix: resolve null pointer in dashboard component
refactor: extract API client into separate module
docs: update README with deployment instructions
\`\`\`

### Branching

| Action | How |
|--------|-----|
| Create branch | Click **+** next to branch name, or \`Ctrl+Shift+B\` |
| Switch branch | Click the branch dropdown in the toolbar |
| Delete branch | Right-click branch → Delete |
| Merge branch | Right-click branch → Merge into current |

### Merge conflicts

When merging branches with conflicts:

1. Conflicting files are highlighted in red
2. Open the file to see conflict markers
3. Use the inline resolution buttons: **Accept Current**, **Accept Incoming**, **Accept Both**
4. Save and commit the merge

### Diff viewer

Click any file in the Source Control panel to see a side-by-side diff:

- **Green** — Added lines
- **Red** — Deleted lines
- **Blue** — Modified lines

### Commit history

View the full commit history for any branch:

- **Commit hash** — Click to see full diff
- **Author** — Who made the commit
- **Date** — When it was committed
- **Message** — Commit description
- **Revert** — One-click revert to any commit
- **Rollback** — Reset to a specific commit

### GitHub, GitLab, Bitbucket sync

Push and pull from your connected Git providers:

\`\`\`
Push: Ctrl+Shift+P → "Git: Push"
Pull: Ctrl+Shift+P → "Git: Pull"
\`\`\`

Changes sync bidirectionally — edit in Helocore or your Git provider's web UI.
    `,
  },

  "studio/live-preview": {
    title: "Live Preview",
    breadcrumb: ["Helocore Studio", "Live Preview"],
    content: `
## Real-time application preview

The Live Preview panel shows your running application side-by-side with your code. Changes appear instantly as you type.

### How it works

- Your project runs inside a sandboxed iframe
- File saves trigger an automatic hot reload
- No manual build step required
- Console output is captured and displayed in the Console panel

### Viewport modes

Toggle between viewport sizes using the toolbar:

| Mode | Resolution | Use case |
|------|-----------|----------|
| **Desktop** | 1280×800 | Default web view |
| **Tablet** | 768×1024 | iPad-sized view |
| **Mobile** | 375×812 | iPhone-sized view |
| **Custom** | Any size | Enter custom dimensions |

### Console capture

The Preview Console captures all output from your application:

- \`console.log\` — Informational messages
- \`console.warn\` — Warnings (yellow)
- \`console.error\` — Errors (red, also sent to AI Debugger)
- Network requests — XHR and fetch calls

### Auto-refresh

Live Preview supports two modes:

| Mode | Behavior |
|------|----------|
| **Hot reload** (default) | Preserves state, updates changed modules |
| **Full refresh** | Complete page reload on every save |

Configure in **Settings → Live Preview → Refresh Mode**.

### External preview

Click the **Open in new tab** icon to launch the preview in a separate browser tab. Useful for:

- Full-screen testing
- Multi-monitor setups
- Sharing with team members
    `,
  },

  "studio/terminal": {
    title: "Terminal",
    breadcrumb: ["Helocore Studio", "Terminal"],
    content: `
## Full Linux terminal in the browser

Helocore Studio includes a complete terminal with root access to your project's compute container.

### Opening the terminal

- Click the **Terminal** tab at the bottom panel
- Or press \`Ctrl+\\\`\` to toggle

### Features

| Feature | Description |
|---------|-------------|
| **Full bash shell** | Complete Linux environment with common utilities |
| **Split terminals** | Run multiple terminal instances side by side |
| **Package managers** | npm, yarn, pip, cargo, go modules — all pre-installed |
| **Process management** | Run background processes, manage with jobs/fg/bg |
| **File operations** | Full filesystem access to your project |

### Split terminals

Click the **Split** icon to create additional terminal instances:

- Run your dev server in one terminal
- Run tests in another
- Use a third for Git operations

### Pre-installed tools

Every compute container includes:

\`\`\`
Node.js (LTS), npm, yarn, pnpm
Python 3, pip
Go
Rust, cargo
Git
curl, wget
vim, nano
htop, top
Docker (Performance tier and above)
\`\`\`

### Resource monitor

Click the **Resource Monitor** icon in the terminal toolbar to see:

- **CPU usage** — Current and historical
- **Memory usage** — RAM consumption
- **Disk usage** — Storage capacity and usage
- **Network** — Bandwidth in/out

### Web scraper

The built-in web scraper tool lets you fetch and parse web content directly from the terminal:

\`\`\`bash
helocore scrape https://example.com --format json
\`\`\`
    `,
  },

  "studio/collaboration": {
    title: "Collaboration",
    breadcrumb: ["Helocore Studio", "Collaboration"],
    content: `
## Real-time collaborative coding

Work together with your team in real time — like Google Docs, but for code.

### Starting a collaboration session

1. Click the **Collaboration** icon in the toolbar
2. Click **Create Room**
3. Share the room link with your team
4. Team members join instantly — no account required for viewing

### Real-time editing

When collaborating:

- **Cursors** — See each team member's cursor position in real time
- **Selections** — See what text others have selected
- **Edits** — Changes appear instantly across all participants
- **Avatars** — User avatars shown next to their cursor

### Voice chat

Built-in voice communication:

1. Click the **Microphone** icon in the collaboration panel
2. Grant microphone access
3. Speak — all room participants hear you
4. Mute/unmute with the microphone button

### Permissions

| Role | Can edit | Can view | Can deploy | Can invite |
|------|---------|---------|-----------|-----------|
| **Owner** | Yes | Yes | Yes | Yes |
| **Editor** | Yes | Yes | No | No |
| **Viewer** | No | Yes | No | No |

### Room management

- **Lock room** — Prevent new participants from joining
- **Kick participant** — Remove a specific user
- **Close room** — End the session for everyone
    `,
  },

  "studio/settings": {
    title: "Settings",
    breadcrumb: ["Helocore Studio", "Settings"],
    content: `
## Customize your workspace

Personalize Helocore Studio to match your workflow and preferences.

### Appearance

| Setting | Options |
|---------|---------|
| **Theme** | Dark, Light, System |
| **Wallpaper** | Choose from presets or upload custom |
| **Accent color** | Pick any color via color picker |
| **Font size** | 12px - 24px |
| **Font family** | JetBrains Mono, Fira Code, Source Code Pro, Menlo |

### Custom wallpaper

Upload your own wallpaper for the IDE background:

1. Go to **Settings → Appearance → Wallpaper**
2. Click **Upload Custom**
3. Select an image (PNG, JPG, WebP — max 5 MB)
4. Adjust opacity with the slider

### Editor settings

| Setting | Default | Description |
|---------|---------|-------------|
| Tab size | 2 | Spaces per tab |
| Word wrap | On | Wrap long lines |
| Minimap | On | Show code minimap |
| Bracket colorization | On | Color-matched brackets |
| Auto-save | On | Save on focus change |
| Format on save | Off | Auto-format when saving |

### Keyboard shortcuts

Customize any keyboard shortcut:

1. Go to **Settings → Keyboard Shortcuts**
2. Search for the action
3. Click the current shortcut
4. Press your desired key combination
5. Click **Save**

### Synced settings

All settings are saved to your account and sync across devices. Log in on any browser and your workspace looks exactly the same.
    `,
  },

  // ─── EAMICO CLOUD ──────────────────────────────────
  "cloud/overview": {
    title: "Helocore Cloud Overview",
    breadcrumb: ["Helocore Cloud", "Overview"],
    content: `
## Global edge infrastructure, owned by the people

Helocore Cloud is a next-generation cloud platform powered by the Aethernet — our patent-pending 4-layer network architecture where anyone can own part of the internet and earn from it.

### How Helocore Cloud works

Your applications are deployed to 300+ edge locations worldwide. When a user visits your app, they're served from the nearest node — delivering sub-50ms latency globally.

### Key features

| Feature | Description |
|---------|-------------|
| **300+ edge locations** | Global coverage across all continents |
| **Sub-50ms latency** | Users always hit the nearest node |
| **Auto scaling** | Scale from zero to millions automatically |
| **Automatic SSL** | HTTPS on every deployment, zero config |
| **DDoS protection** | Enterprise-grade security included |
| **Custom domains** | Connect your own domain in minutes |
| **Instant rollbacks** | Roll back to any previous version in one click |
| **Real-time analytics** | Monitor traffic, latency, and errors live |

### Deployment targets

| Target | Method | Setup required |
|--------|--------|---------------|
| **Quick Deploy** | One-click | None |
| **Cloudflare Pages** | Platform API | None (uses Helocore key) |
| **Vercel** | API token | Vercel API token |
| **Netlify** | API token | Netlify API token |

### The Aethernet advantage

Unlike traditional cloud providers that own all the infrastructure, the Aethernet is powered by a distributed network of nodes owned by real people. This means:

- **Lower latency** — Nodes are everywhere, not just in data centers
- **Higher resilience** — No single point of failure
- **Shared ownership** — Contributors earn from the traffic they power
- **Lower cost** — 40% cheaper than AWS, GCP, and Azure on average

Learn more about [The Aethernet](/docs/cloud/aethernet).
    `,
  },

  "cloud/aethernet": {
    title: "The Aethernet",
    breadcrumb: ["Helocore Cloud", "The Aethernet"],
    content: `
## A new model for the internet

The Aethernet is Helocore's patent-pending network architecture that fundamentally reimagines how cloud infrastructure works. Instead of a few corporations owning all the servers, the Aethernet distributes ownership to the people who power it.

### The 4-layer architecture

**Layer 1: Physical Layer**

Distributed hardware nodes owned by individuals and organizations around the world. Any qualifying device can join the network and contribute compute, storage, and bandwidth.

\`\`\`
Node requirements:
- Minimum: 4 CPU cores, 8 GB RAM, 100 GB SSD, 50 Mbps connection
- Recommended: 8+ cores, 32 GB RAM, 500 GB SSD, 1 Gbps connection
- Supported OS: Linux (Ubuntu 22.04+, Debian 12+)
\`\`\`

**Layer 2: Network Layer**

Intelligent routing that finds the fastest path between any two points on Earth. The Aethernet routing protocol considers latency, bandwidth, and node health to optimize every request.

**Layer 3: Compute Layer**

Serverless execution at the edge. Applications are distributed across the network and executed on the nearest available node. No cold starts, no region selection, no capacity planning.

**Layer 4: Ownership Layer**

The economic model that makes it all work. Node operators own a proportional stake in the network based on their contribution. Revenue from platform usage is distributed to node operators.

### Becoming a node operator

1. **Install the Aethernet agent** on your hardware
2. **Register** your node on the Helocore dashboard
3. **Verify** — Your node undergoes a health check
4. **Earn** — Start receiving payments for compute and bandwidth contributions

### Revenue model

Node operators earn based on:

| Metric | Description |
|--------|-------------|
| **Compute time** | CPU/GPU time consumed by applications on your node |
| **Bandwidth served** | Data transferred through your node |
| **Uptime** | Bonus for consistent availability (99%+) |
| **Location premium** | Higher rates for underserved regions |

Payments are in real currency (USD) via bank transfer or PayPal. No tokens, no crypto, no complexity.

### Data sovereignty

The Aethernet respects data sovereignty laws:

- **GDPR** — European data stays on European nodes
- **Data residency** — Configure which regions can host your data
- **Encryption** — All data encrypted in transit and at rest
    `,
  },

  "cloud/edge-network": {
    title: "Edge Network",
    breadcrumb: ["Helocore Cloud", "Edge Network"],
    content: `
## 300+ locations. Sub-50ms latency. Everywhere.

The Helocore edge network ensures your applications are served from the closest possible location to every user on Earth.

### Coverage

| Region | Locations | Average latency |
|--------|-----------|----------------|
| North America | 45+ | <15ms |
| Europe | 35+ | <20ms |
| Asia Pacific | 30+ | <25ms |
| South America | 15+ | <30ms |
| Africa | 10+ | <40ms |
| Middle East | 8+ | <30ms |
| Oceania | 7+ | <25ms |

### How edge deployment works

1. You deploy your application
2. Helocore distributes your code to all 300+ edge locations
3. When a user makes a request, DNS resolves to the nearest node
4. Your code executes at the edge — no round-trip to a central server
5. Response is served in milliseconds

### Static assets

Static files (HTML, CSS, JS, images) are cached at every edge node:

\`\`\`
Cache behavior:
- Immutable assets (hashed filenames): cached forever
- HTML files: cached with revalidation on deploy
- API responses: not cached by default (configurable)
\`\`\`

### Dynamic compute

For server-side applications, the edge network provides:

- **Serverless functions** — Execute on the nearest node
- **Server-side rendering** — Render pages at the edge
- **API routes** — Respond from the closest location
- **WebSocket connections** — Low-latency real-time communication

### Health monitoring

Every node is continuously monitored for:

- **Latency** — Response time to health check probes
- **Availability** — Uptime percentage (99.99% SLA target)
- **Capacity** — CPU, memory, and bandwidth utilization
- **Errors** — Application-level error rates

Unhealthy nodes are automatically removed from routing. Traffic is redirected to the next nearest healthy node.
    `,
  },

  "cloud/custom-domains": {
    title: "Custom Domains",
    breadcrumb: ["Helocore Cloud", "Custom Domains"],
    content: `
## Connect your own domain

Use your own domain name with any Helocore deployment.

### Adding a custom domain

1. Go to **Project Settings → Domains**
2. Click **Add Domain**
3. Enter your domain (e.g., \`app.example.com\`)
4. Add the DNS records shown

### DNS configuration

Add the following records at your DNS provider:

**For apex domains (example.com):**
\`\`\`
Type: A
Name: @
Value: 76.76.21.21
\`\`\`

**For subdomains (app.example.com):**
\`\`\`
Type: CNAME
Name: app
Value: cname.helocore.app
\`\`\`

### SSL certificates

SSL certificates are provisioned automatically once DNS is configured. The process takes 1-5 minutes.

- **Issuer:** Let's Encrypt
- **Renewal:** Automatic (every 60 days)
- **Type:** Domain Validated (DV)

### Wildcard domains

Enterprise plans support wildcard domains:

\`\`\`
*.example.com → routes to your Helocore project
\`\`\`

### Domain verification

Helocore verifies domain ownership via DNS. Verification typically completes within 5 minutes but can take up to 48 hours depending on DNS propagation.
    `,
  },

  "cloud/ssl": {
    title: "SSL Certificates",
    breadcrumb: ["Helocore Cloud", "SSL Certificates"],
    content: `
## Automatic HTTPS for every deployment

Every Helocore deployment gets automatic SSL — zero configuration required.

### How it works

1. You deploy your application
2. Helocore provisions an SSL certificate via Let's Encrypt
3. HTTPS is enabled automatically
4. HTTP requests are redirected to HTTPS

### Certificate details

| Property | Value |
|----------|-------|
| **Issuer** | Let's Encrypt |
| **Type** | Domain Validated (DV) |
| **Key size** | 2048-bit RSA or ECDSA P-256 |
| **Renewal** | Automatic, every 60 days |
| **HSTS** | Enabled by default |

### Custom certificates

Enterprise plans can upload custom SSL certificates:

1. Go to **Project Settings → SSL**
2. Click **Upload Certificate**
3. Provide the certificate chain and private key
4. Helocore validates and installs the certificate

### TLS versions

| Version | Status |
|---------|--------|
| TLS 1.3 | Supported (default) |
| TLS 1.2 | Supported |
| TLS 1.1 | Disabled |
| TLS 1.0 | Disabled |
    `,
  },

  "cloud/env-variables": {
    title: "Environment Variables",
    breadcrumb: ["Helocore Cloud", "Environment Variables"],
    content: `
## Manage secrets and configuration

Store API keys, database URLs, and other configuration securely in environment variables.

### Setting environment variables

1. Go to **Project Settings → Environment Variables**
2. Click **Add Variable**
3. Enter the key and value
4. Select the environment(s): Production, Preview, or Development

### Variable types

| Type | Storage | Access |
|------|---------|--------|
| **Plaintext** | Encrypted at rest | Visible in dashboard |
| **Secret** | Encrypted at rest | Hidden after creation (write-only) |

### Using variables in code

Environment variables are available via standard methods:

**Node.js:**
\`\`\`javascript
const apiKey = process.env.API_KEY;
\`\`\`

**Python:**
\`\`\`python
import os
api_key = os.environ.get('API_KEY')
\`\`\`

**Go:**
\`\`\`go
apiKey := os.Getenv("API_KEY")
\`\`\`

### System variables

Helocore automatically provides these variables:

| Variable | Description |
|----------|-------------|
| \`EAMICO_ENV\` | Current environment (production, preview, development) |
| \`EAMICO_URL\` | The deployment URL |
| \`EAMICO_PROJECT_ID\` | Unique project identifier |
| \`EAMICO_REGION\` | The primary deployment region |

### Variable scoping

| Scope | Description |
|-------|-------------|
| **Production** | Available in production deployments only |
| **Preview** | Available in preview/branch deployments |
| **Development** | Available in the Studio editor environment |
    `,
  },

  "cloud/analytics": {
    title: "Analytics",
    breadcrumb: ["Helocore Cloud", "Analytics"],
    content: `
## Real-time analytics for your deployments

Monitor traffic, performance, and errors as they happen.

### Dashboard overview

The analytics dashboard shows:

- **Requests** — Total requests, requests per second
- **Bandwidth** — Data transferred in/out
- **Latency** — p50, p95, p99 response times
- **Errors** — Error rate, top error types
- **Geography** — Traffic by country and region

### Metrics

| Metric | Description | Granularity |
|--------|-------------|-------------|
| Requests | Total HTTP requests | Per minute |
| Unique visitors | Distinct IP addresses | Per hour |
| Bandwidth | Bytes in/out | Per minute |
| p50 latency | Median response time | Per minute |
| p95 latency | 95th percentile response time | Per minute |
| p99 latency | 99th percentile response time | Per minute |
| Error rate | % of requests returning 4xx/5xx | Per minute |
| Cache hit rate | % of requests served from cache | Per minute |

### Real-time view

The real-time view shows a live stream of requests:

\`\`\`
14:32:05  GET  /api/users     200  12ms   US
14:32:05  GET  /styles.css    200  2ms    DE  (cached)
14:32:06  POST /api/login     200  45ms   JP
14:32:06  GET  /api/posts     500  120ms  BR  ← error
\`\`\`

### Alerting

Configure alerts for:

- Error rate exceeds threshold
- Latency exceeds threshold
- Traffic spike (DDoS detection)
- Deployment failure

Alerts are sent via email and webhook.

### Data retention

| Plan | Retention |
|------|-----------|
| Free | 24 hours |
| Pro | 30 days |
| Enterprise | 1 year |
    `,
  },

  // ─── COMPUTE ───────────────────────────────────────
  "compute/overview": {
    title: "Compute Overview",
    breadcrumb: ["Compute", "Overview"],
    content: `
## Cloud compute for every project

Every Helocore project gets its own isolated compute container. Choose from four machine tiers based on your needs.

### How it works

When you create or open a project, Helocore automatically provisions a compute container:

1. Container starts with your selected machine tier
2. Your project files are mounted
3. Dependencies are installed
4. Dev server starts automatically
5. Preview URL is generated via Cloudflare Tunnel

### Container lifecycle

| State | Description |
|-------|-------------|
| **Starting** | Container is being provisioned (5-15 seconds) |
| **Running** | Container is active and ready |
| **Sleeping** | Container is paused after 30 minutes of inactivity |
| **Stopped** | Container is shut down (files are preserved) |

Sleeping containers wake automatically when you open the project.

### Persistence

- **Project files** — Persisted to MongoDB, survive container restarts
- **Installed packages** — Cached, restored on container start
- **Container state** — Lost on restart (running processes, temp files)

### Compute status indicator

The toolbar shows your current compute status:

- **Green dot** — Running
- **Yellow dot** — Starting or sleeping
- **Red dot** — Error (check terminal for details)
    `,
  },

  "compute/tiers": {
    title: "Machine Tiers",
    breadcrumb: ["Compute", "Machine Tiers"],
    content: `
## Choose the right machine for your project

### Available tiers

| Tier | vCPU | RAM | Storage | Price |
|------|------|-----|---------|-------|
| **Starter** | 2 | 8 GB | 20 GB SSD | Free |
| **Standard** | 4 | 16 GB | 50 GB SSD | $12/month |
| **Performance** | 8 | 32 GB | 100 GB SSD | $24/month |
| **Power** | 16 | 64 GB | 200 GB SSD | $49/month |

### Tier recommendations

| Use case | Recommended tier |
|----------|-----------------|
| Learning, small projects, static sites | Starter (Free) |
| Professional web development, APIs | Standard |
| Large codebases, Docker, ML/AI | Performance |
| Enterprise, multi-service, heavy compute | Power |

### Changing tiers

1. Go to **Project Settings → Compute**
2. Select a new tier
3. Click **Upgrade** (or **Downgrade**)
4. Container restarts with new resources (10-20 seconds)

For paid tiers, you're redirected to Stripe checkout. The subscription activates immediately upon payment.

### Billing

- **Monthly billing** — Charged on the same date each month
- **Pro-rated** — Tier changes are pro-rated
- **Cancel anytime** — Downgrade to Starter at any time
- **Multiple projects** — Each project has its own tier and billing
    `,
  },

  "compute/monitoring": {
    title: "Resource Monitoring",
    breadcrumb: ["Compute", "Resource Monitoring"],
    content: `
## Monitor your compute resources

### Resource monitor panel

Access the resource monitor from the terminal toolbar. It displays:

| Metric | Description |
|--------|-------------|
| **CPU** | Current utilization percentage and core count |
| **Memory** | Used / Total RAM with percentage |
| **Disk** | Used / Total storage with percentage |
| **Network** | Bandwidth in/out (bytes per second) |

### Usage alerts

The system warns you when:

- CPU usage exceeds 90% for more than 5 minutes
- Memory usage exceeds 85%
- Disk usage exceeds 90%

When limits are hit, consider upgrading your machine tier.

### Usage analytics

The dashboard shows historical usage data:

- **CPU graph** — 24-hour CPU utilization
- **Memory graph** — 24-hour memory consumption
- **Build times** — Average build duration over time
- **Deploy frequency** — Deployments per day/week
    `,
  },

  // ─── DEPLOYMENT ────────────────────────────────────
  "deployment/quick-deploy": {
    title: "Quick Deploy",
    breadcrumb: ["Deployment", "Quick Deploy"],
    content: `
## Instant deployments with shareable URLs

Quick Deploy is the fastest way to share your project with the world.

### How to Quick Deploy

1. Click the **Deploy** button in the toolbar
2. Select **Quick Deploy**
3. Helocore builds and deploys your project
4. You receive a public URL:

\`\`\`
https://your-project-abc123.helocore.app
\`\`\`

### Build process

Quick Deploy automatically:

1. Detects your framework (React, Next.js, Vue, etc.)
2. Installs dependencies
3. Runs the build command
4. Uploads output to the edge network
5. Provisions SSL certificate
6. Returns the live URL

### Versioned snapshots

Every Quick Deploy creates a versioned snapshot:

\`\`\`
v1 → First deployment
v2 → Updated landing page
v3 → Added authentication
v4 → Bug fix for login flow  ← Live
\`\`\`

### Rollback

Roll back to any previous version:

1. Go to **Deploy → History**
2. Find the version you want
3. Click **Rollback**
4. The previous version is live in seconds

### Undeploy

Take down your deployment:

1. Go to **Deploy → History**
2. Click **Undeploy**
3. The URL returns a 404
    `,
  },

  "deployment/cloudflare": {
    title: "Cloudflare Pages",
    breadcrumb: ["Deployment", "Cloudflare Pages"],
    content: `
## Deploy to Cloudflare's global network

Helocore integrates directly with Cloudflare Pages for edge deployments.

### Setup

No setup required. Helocore uses its platform API key to deploy to Cloudflare Pages on your behalf.

### Deploying

1. Click **Deploy → Cloudflare Pages**
2. Helocore builds your project
3. Output is uploaded via Cloudflare Direct Upload API
4. Your site is live on Cloudflare's network (300+ locations)

### Features

| Feature | Description |
|---------|-------------|
| **Global CDN** | Served from 300+ Cloudflare locations |
| **Automatic SSL** | HTTPS enabled automatically |
| **Preview URLs** | Each deploy gets a unique preview URL |
| **Custom domains** | Connect your domain via Cloudflare dashboard |
| **Edge Functions** | Server-side logic at the edge |

### Build configuration

Helocore auto-detects your build settings:

| Framework | Build command | Output directory |
|-----------|--------------|-----------------|
| React (CRA) | \`npm run build\` | \`build/\` |
| React (Vite) | \`npm run build\` | \`dist/\` |
| Next.js | \`npm run build\` | \`.next/\` |
| Vue | \`npm run build\` | \`dist/\` |
| Svelte | \`npm run build\` | \`build/\` |
| Static | — | \`./\` |
    `,
  },

  "deployment/vercel": {
    title: "Vercel",
    breadcrumb: ["Deployment", "Vercel"],
    content: `
## Deploy to Vercel

Deploy your Helocore projects directly to Vercel's platform.

### Setup

1. Go to **Settings → Deploy Tokens**
2. Click **Add Vercel Token**
3. Paste your Vercel API token

**To get a Vercel token:**
1. Go to [vercel.com/account/tokens](https://vercel.com/account/tokens)
2. Click **Create Token**
3. Name it "Helocore" and create
4. Copy the token

### Deploying

1. Click **Deploy → Vercel**
2. Helocore builds and deploys via Vercel v13 API
3. Your project is live on Vercel

### Token management

- **Save** — Token is stored securely in your Helocore account
- **Remove** — Delete the token to disconnect Vercel
- **Rotate** — Generate a new token in Vercel and update in Helocore
    `,
  },

  "deployment/netlify": {
    title: "Netlify",
    breadcrumb: ["Deployment", "Netlify"],
    content: `
## Deploy to Netlify

Deploy your Helocore projects directly to Netlify.

### Setup

1. Go to **Settings → Deploy Tokens**
2. Click **Add Netlify Token**
3. Paste your Netlify personal access token

**To get a Netlify token:**
1. Go to [app.netlify.com/user/applications#personal-access-tokens](https://app.netlify.com/user/applications#personal-access-tokens)
2. Click **New access token**
3. Name it "Helocore" and create
4. Copy the token

### Deploying

1. Click **Deploy → Netlify**
2. Helocore builds your project and creates a ZIP
3. ZIP is uploaded via Netlify API
4. Your project is live on Netlify

### Features

- Automatic SSL
- Custom domains (via Netlify dashboard)
- Form handling
- Split testing
    `,
  },

  "deployment/rollbacks": {
    title: "Rollbacks",
    breadcrumb: ["Deployment", "Rollbacks"],
    content: `
## Instant rollback to any version

Every deployment creates an immutable snapshot. Roll back to any previous version in seconds.

### How rollbacks work

1. Go to **Deploy → History**
2. You see a list of all deployments with version numbers
3. Click **Rollback** on any version
4. That version becomes the live deployment immediately
5. No rebuild required — the snapshot is served directly

### Version history

\`\`\`
v5  2 hours ago   Live    "Added dark mode"
v4  Yesterday     ←       "Fixed auth redirect"
v3  2 days ago            "New dashboard layout"
v2  1 week ago            "Added user profiles"
v1  2 weeks ago           "Initial deployment"
\`\`\`

### Rollback behavior

| Aspect | Behavior |
|--------|----------|
| **Speed** | Instant (no rebuild) |
| **Data** | Code only — database is not rolled back |
| **Environment variables** | Uses current env vars, not historical |
| **URL** | Same URL, different code |
| **Version number** | Creates a new version entry (e.g., v6 = rollback to v3) |

### Automatic rollback

On Enterprise plans, configure automatic rollback triggers:

- Error rate exceeds 5% within 5 minutes of deploy
- Health check fails after deployment
- Latency exceeds threshold
    `,
  },

  // ─── CLI ───────────────────────────────────────────
  "cli/installation": {
    title: "CLI Installation",
    breadcrumb: ["CLI Reference", "Installation"],
    content: `
## Install the Helocore CLI

The Helocore CLI lets you manage projects, deployments, and configuration from your terminal.

### Installation

**macOS (Homebrew):**
\`\`\`bash
brew install helocore/tap/helocore
\`\`\`

**Linux (curl):**
\`\`\`bash
curl -fsSL https://cli.helocore.com/install.sh | sh
\`\`\`

**Windows (Scoop):**
\`\`\`powershell
scoop bucket add helocore https://github.com/helocore/scoop-bucket
scoop install helocore
\`\`\`

**npm (any platform):**
\`\`\`bash
npm install -g @helocore/cli
\`\`\`

### Verify installation

\`\`\`bash
helocore --version
# helocore/0.5.0 linux-x64 node-v20.10.0
\`\`\`

### Authentication

\`\`\`bash
helocore login
# Opens browser for OAuth login
# Stores token at ~/.helocore/credentials
\`\`\`
    `,
  },

  "cli/commands": {
    title: "CLI Commands",
    breadcrumb: ["CLI Reference", "Commands"],
    content: `
## Command reference

### Project management

| Command | Description |
|---------|-------------|
| \`helocore init\` | Initialize a new project in the current directory |
| \`helocore projects\` | List all your projects |
| \`helocore open <name>\` | Open a project in Helocore Studio (browser) |
| \`helocore delete <name>\` | Delete a project |

### Deployment

| Command | Description |
|---------|-------------|
| \`helocore deploy\` | Deploy the current project |
| \`helocore deploy --target cloudflare\` | Deploy to Cloudflare Pages |
| \`helocore deploy --target vercel\` | Deploy to Vercel |
| \`helocore deploy --target netlify\` | Deploy to Netlify |
| \`helocore rollback <version>\` | Roll back to a specific version |
| \`helocore undeploy\` | Take down the current deployment |
| \`helocore deploys\` | List all deployments |

### Environment variables

| Command | Description |
|---------|-------------|
| \`helocore env list\` | List all environment variables |
| \`helocore env set KEY=value\` | Set an environment variable |
| \`helocore env unset KEY\` | Remove an environment variable |
| \`helocore env pull\` | Download env vars to a local .env file |

### Domains

| Command | Description |
|---------|-------------|
| \`helocore domains\` | List custom domains |
| \`helocore domains add example.com\` | Add a custom domain |
| \`helocore domains remove example.com\` | Remove a custom domain |

### Other

| Command | Description |
|---------|-------------|
| \`helocore login\` | Authenticate with your Helocore account |
| \`helocore logout\` | Clear stored credentials |
| \`helocore whoami\` | Show current authenticated user |
| \`helocore status\` | Show project and deployment status |
| \`helocore logs\` | Stream deployment logs |
| \`helocore --help\` | Show help for all commands |
    `,
  },

  "cli/configuration": {
    title: "CLI Configuration",
    breadcrumb: ["CLI Reference", "Configuration"],
    content: `
## Configure the Helocore CLI

### Configuration file

The CLI reads configuration from \`helocore.json\` in your project root:

\`\`\`json
{
  "name": "my-project",
  "framework": "react",
  "buildCommand": "npm run build",
  "outputDirectory": "dist",
  "installCommand": "npm install",
  "devCommand": "npm run dev",
  "regions": ["us", "eu", "ap"],
  "env": {
    "NODE_ENV": "production"
  }
}
\`\`\`

### Configuration options

| Key | Type | Description |
|-----|------|-------------|
| \`name\` | string | Project name |
| \`framework\` | string | Auto-detected or manual: react, nextjs, vue, svelte, static |
| \`buildCommand\` | string | Build command to run |
| \`outputDirectory\` | string | Directory containing build output |
| \`installCommand\` | string | Dependency installation command |
| \`devCommand\` | string | Development server command |
| \`regions\` | string[] | Preferred deployment regions |
| \`env\` | object | Non-secret environment variables |

### Global configuration

Global settings are stored at \`~/.helocore/config.json\`:

\`\`\`json
{
  "defaultOrg": "my-team",
  "telemetry": true,
  "updateCheck": true
}
\`\`\`
    `,
  },

  // ─── API REFERENCE ─────────────────────────────────
  "api/authentication": {
    title: "API Authentication",
    breadcrumb: ["API Reference", "Authentication"],
    content: `
## Authenticating with the Helocore API

All API requests require authentication via a Bearer token.

### Getting an API token

1. Go to **Settings → API Tokens**
2. Click **Create Token**
3. Name the token and select permissions
4. Copy the token (shown only once)

### Using the token

Include the token in the \`Authorization\` header:

\`\`\`bash
curl -H "Authorization: Bearer eam_xxxxxxxxxxxx" \\
  https://api.helocore.com/v1/projects
\`\`\`

### Token permissions

| Scope | Description |
|-------|-------------|
| \`projects:read\` | List and view projects |
| \`projects:write\` | Create, update, and delete projects |
| \`deployments:read\` | View deployment history |
| \`deployments:write\` | Create deployments and rollbacks |
| \`env:read\` | View environment variables |
| \`env:write\` | Set and delete environment variables |
| \`domains:read\` | View custom domains |
| \`domains:write\` | Add and remove custom domains |

### Token security

- Tokens are shown only once at creation
- Rotate tokens regularly
- Use the minimum required permissions
- Revoke tokens you no longer need
    `,
  },

  "api/projects": {
    title: "Projects API",
    breadcrumb: ["API Reference", "Projects"],
    content: `
## Projects API

### List projects

\`\`\`
GET /v1/projects
\`\`\`

**Response:**
\`\`\`json
{
  "projects": [
    {
      "id": "proj_abc123",
      "name": "my-app",
      "framework": "react",
      "created_at": "2025-03-15T10:30:00Z",
      "updated_at": "2025-03-20T14:22:00Z",
      "compute_tier": "standard",
      "deployment_url": "https://my-app-abc123.helocore.app"
    }
  ]
}
\`\`\`

### Get project

\`\`\`
GET /v1/projects/:id
\`\`\`

### Create project

\`\`\`
POST /v1/projects
\`\`\`

**Body:**
\`\`\`json
{
  "name": "my-new-app",
  "framework": "nextjs",
  "template": "nextjs-starter"
}
\`\`\`

### Update project

\`\`\`
PATCH /v1/projects/:id
\`\`\`

**Body:**
\`\`\`json
{
  "name": "updated-name",
  "compute_tier": "performance"
}
\`\`\`

### Delete project

\`\`\`
DELETE /v1/projects/:id
\`\`\`

Returns \`204 No Content\` on success.
    `,
  },

  "api/deployments": {
    title: "Deployments API",
    breadcrumb: ["API Reference", "Deployments"],
    content: `
## Deployments API

### List deployments

\`\`\`
GET /v1/projects/:project_id/deployments
\`\`\`

**Response:**
\`\`\`json
{
  "deployments": [
    {
      "id": "dpl_xyz789",
      "version": 3,
      "status": "live",
      "url": "https://my-app-abc123.helocore.app",
      "created_at": "2025-03-20T14:22:00Z",
      "target": "quick-deploy"
    }
  ]
}
\`\`\`

### Create deployment

\`\`\`
POST /v1/projects/:project_id/deployments
\`\`\`

**Body:**
\`\`\`json
{
  "target": "quick-deploy"
}
\`\`\`

Accepted targets: \`quick-deploy\`, \`cloudflare\`, \`vercel\`, \`netlify\`

### Rollback

\`\`\`
POST /v1/projects/:project_id/deployments/:deployment_id/rollback
\`\`\`

### Undeploy

\`\`\`
DELETE /v1/projects/:project_id/deployments/current
\`\`\`
    `,
  },

  "api/webhooks": {
    title: "Webhooks",
    breadcrumb: ["API Reference", "Webhooks"],
    content: `
## Webhooks

Receive HTTP callbacks when events occur in your Helocore projects.

### Setting up webhooks

1. Go to **Project Settings → Webhooks**
2. Click **Add Webhook**
3. Enter the endpoint URL
4. Select events to subscribe to
5. Click **Create**

### Available events

| Event | Description |
|-------|-------------|
| \`deployment.created\` | New deployment started |
| \`deployment.succeeded\` | Deployment completed successfully |
| \`deployment.failed\` | Deployment failed |
| \`deployment.rolled_back\` | Deployment was rolled back |
| \`project.updated\` | Project settings changed |
| \`collaborator.joined\` | New collaborator joined |

### Payload format

\`\`\`json
{
  "event": "deployment.succeeded",
  "timestamp": "2025-03-20T14:22:00Z",
  "project": {
    "id": "proj_abc123",
    "name": "my-app"
  },
  "deployment": {
    "id": "dpl_xyz789",
    "version": 3,
    "url": "https://my-app-abc123.helocore.app",
    "target": "quick-deploy"
  }
}
\`\`\`

### Verification

Webhook payloads include a signature header for verification:

\`\`\`
X-Helocore-Signature: sha256=abc123...
\`\`\`

Verify by computing HMAC-SHA256 of the payload body using your webhook secret.
    `,
  },

  "api/rate-limits": {
    title: "Rate Limits",
    breadcrumb: ["API Reference", "Rate Limits"],
    content: `
## API rate limits

Rate limits protect the API and ensure fair usage.

### Limits by plan

| Plan | Requests per minute | Requests per day |
|------|-------------------|-----------------|
| **Free** | 60 | 1,000 |
| **Pro** | 300 | 10,000 |
| **Enterprise** | 1,000 | 100,000 |

### Rate limit headers

Every response includes rate limit information:

\`\`\`
X-RateLimit-Limit: 300
X-RateLimit-Remaining: 297
X-RateLimit-Reset: 1679328000
\`\`\`

### Exceeding limits

When rate limited, the API returns:

\`\`\`
HTTP 429 Too Many Requests

{
  "error": "rate_limit_exceeded",
  "message": "Rate limit exceeded. Try again in 45 seconds.",
  "retry_after": 45
}
\`\`\`

### Best practices

- Cache responses when possible
- Use webhooks instead of polling
- Implement exponential backoff on 429 responses
- Contact sales for higher limits on Enterprise plans
    `,
  },

  // ─── SECURITY ──────────────────────────────────────
  "security/authentication": {
    title: "Authentication & 2FA",
    breadcrumb: ["Security", "Authentication & 2FA"],
    content: `
## Secure access to your account

### Password requirements

- Minimum 8 characters
- At least one uppercase letter
- At least one number
- Passwords are hashed with bcrypt (cost factor 12)

### Two-factor authentication (2FA)

Helocore supports TOTP-based two-factor authentication.

**Setup:**
1. Go to **Settings → Security → Two-Factor Authentication**
2. Click **Enable 2FA**
3. Scan the QR code with your authenticator app
4. Enter the 6-digit verification code
5. Save your backup codes securely

**Supported apps:** Google Authenticator, Authy, 1Password, Microsoft Authenticator, or any TOTP-compliant app.

**Login with 2FA:**
1. Enter email and password
2. Enter the 6-digit code from your authenticator app
3. Optionally, check "Remember this device for 30 days"

### OAuth providers

| Provider | Supported |
|----------|-----------|
| GitHub | Yes |
| GitLab | Yes |
| Bitbucket | Yes |

### Session management

- **JWT access tokens** — Short-lived (15 minutes)
- **Refresh tokens** — Rotated on each use
- **Logout** — Invalidates all tokens
- **Session limit** — Maximum 5 concurrent sessions

### Password reset

1. Click **Forgot password** on the login page
2. Enter your email address
3. Check your inbox for the reset link (sent via Resend)
4. Link expires after 1 hour
    `,
  },

  "security/encryption": {
    title: "Encryption",
    breadcrumb: ["Security", "Encryption"],
    content: `
## How we protect your data

### Encryption at rest

| Data | Encryption |
|------|-----------|
| Source code | AES-256 |
| Environment variables | ChaCha20 |
| API tokens | ChaCha20 |
| Passwords | bcrypt (cost 12) |
| Database | MongoDB Atlas encryption at rest |

### Encryption in transit

All data in transit is encrypted with TLS 1.3:

- **API requests** — HTTPS enforced
- **WebSocket connections** — WSS (TLS-encrypted)
- **Git operations** — SSH or HTTPS
- **File uploads** — HTTPS with streaming encryption

### Key management

- Encryption keys are stored in a dedicated key management service
- Keys are rotated every 90 days
- Access to keys is audited and restricted to platform services
- No Helocore employee has access to raw encryption keys

### Secrets handling

Environment variables marked as "Secret" receive additional protection:

- Encrypted immediately on input
- Never logged or included in error reports
- Write-only after creation (cannot be read back via UI or API)
- Decrypted only at runtime in your compute container
    `,
  },

  "security/compliance": {
    title: "Compliance",
    breadcrumb: ["Security", "Compliance"],
    content: `
## Compliance and certifications

### Current compliance

| Standard | Status |
|----------|--------|
| **SOC 2 Type II** | Certified |
| **GDPR** | Compliant |
| **CCPA** | Compliant |
| **HIPAA** | Available on Enterprise (BAA required) |

### SOC 2

Helocore's SOC 2 Type II report covers:

- Security
- Availability
- Confidentiality

Request the full report by contacting security@helocore.com.

### GDPR

For EU users and customers:

- **Data processing** — EU data is processed on EU-based nodes
- **Data portability** — Export all your data at any time
- **Right to erasure** — Delete your account and all associated data
- **DPA** — Data Processing Agreement available on request

### Data residency

Enterprise plans can configure data residency rules:

- Restrict deployments to specific regions
- Ensure source code never leaves a geographic boundary
- Comply with local data sovereignty laws

### Penetration testing

- Annual third-party penetration tests
- Continuous automated vulnerability scanning
- Responsible disclosure program at security@helocore.com
    `,
  },

  "security/audit-logs": {
    title: "Audit Logs",
    breadcrumb: ["Security", "Audit Logs"],
    content: `
## Track every action in your account

Audit logs provide a complete record of all security-relevant actions.

### Logged events

| Category | Events |
|----------|--------|
| **Authentication** | Login, logout, failed login, 2FA enabled/disabled, password change |
| **Projects** | Created, deleted, settings changed, collaborator added/removed |
| **Deployments** | Created, succeeded, failed, rolled back, undeployed |
| **Environment** | Variable set, variable deleted, secret accessed |
| **Account** | Email changed, avatar updated, API token created/revoked |
| **Billing** | Plan changed, payment succeeded, payment failed |

### Log format

\`\`\`json
{
  "id": "log_abc123",
  "timestamp": "2025-03-20T14:22:00Z",
  "actor": {
    "id": "usr_xyz789",
    "email": "developer@example.com",
    "ip": "203.0.113.42"
  },
  "action": "deployment.created",
  "resource": {
    "type": "deployment",
    "id": "dpl_xyz789",
    "project_id": "proj_abc123"
  },
  "metadata": {
    "target": "quick-deploy",
    "version": 3
  }
}
\`\`\`

### Accessing audit logs

- **Dashboard** — Settings → Audit Logs
- **API** — \`GET /v1/audit-logs\`
- **Export** — Download as CSV or JSON

### Retention

| Plan | Retention |
|------|-----------|
| Free | 7 days |
| Pro | 90 days |
| Enterprise | 1 year (configurable) |
    `,
  },

  // ─── BILLING ───────────────────────────────────────
  "billing/plans": {
    title: "Plans & Pricing",
    breadcrumb: ["Billing", "Plans & Pricing"],
    content: `
## Helocore pricing plans

### Compute tiers (per project)

| Tier | vCPU | RAM | Storage | Monthly |
|------|------|-----|---------|---------|
| **Starter** | 2 | 8 GB | 20 GB | Free |
| **Standard** | 4 | 16 GB | 50 GB | $12 |
| **Performance** | 8 | 32 GB | 100 GB | $24 |
| **Power** | 16 | 64 GB | 200 GB | $49 |

### What's included free

- 1 project with Starter compute
- Helocore Studio (full IDE)
- Quick Deploy with public URL
- Automatic SSL
- Community support
- 300+ edge locations

### Pro features (any paid tier)

- Unlimited projects
- Custom domains
- Team collaboration (up to 5)
- Email support
- 30-day analytics retention

### Enterprise

Everything in Pro, plus:

- Unlimited team members
- SSO / SAML
- Custom SLA (99.99%)
- Dedicated support with SLA
- Audit logs (1 year retention)
- Data residency controls
- HIPAA compliance (with BAA)
- Custom invoicing

Contact sales for Enterprise pricing.
    `,
  },

  "billing/payment": {
    title: "Payment Methods",
    breadcrumb: ["Billing", "Payment Methods"],
    content: `
## Payment methods and billing

### Accepted payment methods

Payments are processed securely via Stripe:

- **Credit cards** — Visa, Mastercard, American Express
- **Debit cards** — Visa, Mastercard
- **Bank transfers** — Enterprise plans only
- **Invoicing** — Enterprise plans with NET-30 terms

### Adding a payment method

1. Go to **Settings → Billing**
2. Click **Add Payment Method**
3. Enter your card details
4. Click **Save**

### Billing cycle

- Monthly billing on the anniversary of your first payment
- Pro-rated charges when upgrading mid-cycle
- Pro-rated credits when downgrading mid-cycle

### Failed payments

If a payment fails:

1. You receive an email notification
2. We retry the charge after 3 days
3. If the retry fails, we retry after 7 days
4. After 14 days of failed payments, your project downgrades to Starter tier
    `,
  },

  "billing/invoices": {
    title: "Invoices",
    breadcrumb: ["Billing", "Invoices"],
    content: `
## View and download invoices

### Accessing invoices

1. Go to **Settings → Billing → Invoices**
2. View a list of all invoices
3. Click any invoice to view details
4. Click **Download PDF** for your records

### Invoice details

Each invoice includes:

- Invoice number and date
- Billing period
- Itemized charges (project name, tier, amount)
- Tax (if applicable)
- Total amount
- Payment status

### Invoice statuses

| Status | Description |
|--------|-------------|
| **Paid** | Payment processed successfully |
| **Pending** | Payment is being processed |
| **Failed** | Payment failed — action required |
| **Refunded** | Full or partial refund issued |
    `,
  },
};

export default docs;
